// Company-wide content: identity, contact details, navigation, the four service lines,
// principles, approach and delivery process. Service page detail lives in ./services/.
import {
  Cloud, Boxes, Lightbulb, Bot,
  FileSignature, Users, ClipboardList, CheckCircle, Code, MessageSquare, Rocket
} from 'lucide-react';

export const company = {
  name: 'Dreamvision Solutions',
  shortName: 'Dreamvision',
  email: 'info@dreamvisionsolutions.com',
  phone: '+91 9226333900',
  phoneHref: 'tel:+919226333900',
  city: 'Mumbai',
  region: 'Maharashtra, India',
  address: 'Dreamvision Solutions, Mumbai, India',
  timeZone: 'Asia/Kolkata',
  tagline: 'Transforming Business Through AI-Driven Technology',
  description:
    'Leading AI-powered IT solutions provider specializing in intelligent Salesforce development, AI-enhanced ERP systems, custom AI agents, and functional consultancy for enterprises worldwide.',
  slogan: ['Fast.', 'Scalable.', 'Secure.']
};

// slug ↔ route ↔ blog category for each service line. Text is the site's existing copy.
export const services = [
  {
    slug: 'salesforce',
    path: '/salesforce',
    index: '01',
    name: 'Salesforce',
    label: 'Salesforce',
    title: 'AI-Powered Salesforce Development',
    description:
      'Custom AI-enhanced Salesforce solutions with intelligent automation designed to transform your customer relationship management and drive exponential business growth.',
    features: [
      'Claudeforce & Agentforce Expertise',
      'AI-Driven Custom Salesforce Solutions',
      'Intelligent Sales, Service & Marketing Cloud',
      'AI-Powered Salesforce Integration',
      'Smart Migration & AI Support'
    ],
    blogCategory: 'ai-powered-salesforce',
    icon: Cloud
  },
  {
    slug: 'erp',
    path: '/erp',
    index: '02',
    name: 'ERP',
    label: 'ERP Solutions',
    title: 'AI-Enhanced ERP Product Development',
    description:
      'End-to-end AI-powered ERP solutions with intelligent automation that streamline operations and provide real-time predictive insights for data-driven decisions.',
    features: [
      'AI-Driven ERP Development',
      'Smart Modules (HR, Finance, SCM, CRM)',
      'AI-Enhanced Cloud-based ERP',
      'Intelligent Mobile ERP Interfaces'
    ],
    blogCategory: 'ai-enhanced-erp',
    icon: Boxes
  },
  {
    slug: 'consultancy',
    path: '/consultancy',
    index: '03',
    name: 'Consultancy',
    label: 'Functional Consultancy',
    title: 'AI-Driven Functional Consultancy',
    description:
      'Expert AI-powered business analysis and intelligent requirement engineering to ensure your software delivers maximum value through automation.',
    features: [
      'AI-Assisted Business Process Analysis',
      'Intelligent Requirement Engineering',
      'AI-Powered Functional Specification Design',
      'Smart Agile/Scrum Integration'
    ],
    blogCategory: 'ai-driven-consultancy',
    icon: Lightbulb
  },
  {
    slug: 'ai-agents',
    path: '/ai-agents',
    index: '04',
    name: 'AI Agents',
    label: 'AI Agents & Local LLMs',
    title: 'Custom AI Agents & Local LLM Solutions',
    description:
      'Build intelligent AI agents and deploy on-premise LLMs for secure, AI-driven automation while keeping your data private and protected.',
    features: [
      'Custom AI Agent Development',
      'Local LLM Setup & Deployment',
      'AI-Powered Data Privacy & Security',
      'Secure On-Premise AI Infrastructure'
    ],
    blogCategory: 'custom-ai-agents',
    icon: Bot
  }
];

export const serviceBySlug = Object.fromEntries(services.map(s => [s.slug, s]));
export const serviceByBlogCategory = Object.fromEntries(services.map(s => [s.blogCategory, s]));

export const mainNav = [
  { path: '/about', label: 'About' },
  { path: '/blog', label: 'Insights' },
  { path: '/contact', label: 'Contact' }
];

export const principles = [
  {
    title: 'Fast',
    description:
      'AI-accelerated development cycles with intelligent automation ensure quick time-to-market without compromising quality.'
  },
  {
    title: 'Scalable',
    description:
      'Build AI-powered solutions that grow intelligently with your business, handling increasing demands effortlessly through machine learning.'
  },
  {
    title: 'Secure',
    description:
      'AI-enhanced enterprise-grade security measures with intelligent threat detection protect your data and ensure compliance.'
  }
];

export const approaches = [
  {
    title: 'Custom Tailored Solutions',
    description:
      "Every business is unique, and so are our solutions. We don't believe in one-size-fits-all. Instead, we carefully craft each project to align perfectly with your specific goals and challenges."
  },
  {
    title: 'Adaptive Methodology',
    description:
      "Our process is flexible and client-centric. We listen, adapt, and evolve our approach based on your feedback, ensuring you're always in control of your project's direction."
  },
  {
    title: 'Partnership Over Transaction',
    description:
      'We build lasting relationships, not just software. Our team becomes an extension of yours, invested in your long-term success and growth beyond project delivery.'
  }
];

export const journeyIntro =
  "We're not just another IT service provider. We're your dedicated technology partner, adapting our process to fit your unique needs at every step.";

export const processSteps = [
  { phase: 'Initiation', title: 'Contract Signed', description: 'Partnership begins with clear terms and mutual commitment to success', icon: FileSignature },
  { phase: 'Planning', title: 'Kickoff Meeting', description: 'Align vision, set expectations, and introduce our dedicated team', icon: Users },
  { phase: 'Discovery', title: 'Requirement Gathering', description: 'Deep dive into your business processes and technical needs', icon: ClipboardList },
  { phase: 'Strategy', title: 'Functional Consultancy', description: 'Expert guidance on best practices and optimal solution architecture', icon: Lightbulb },
  { phase: 'Approval', title: 'Requirements Sign-off', description: 'Finalize scope with complete clarity before development begins', icon: CheckCircle },
  { phase: 'Execution', title: 'Development', description: 'Agile development with regular updates and milestone reviews', icon: Code },
  { phase: 'Refinement', title: 'Client Feedback', description: 'Iterative refinement based on your testing and input', icon: MessageSquare },
  { phase: 'Launch', title: 'Go Live', description: 'Smooth deployment with comprehensive support and documentation', icon: Rocket }
];

// Technologies named across the service pages, for the stack marquee
export const technologies = [
  'Salesforce', 'Agentforce', 'Data 360', 'Sales Cloud', 'Service Cloud', 'Experience Cloud', 'Slack', 'Apex', 'Lightning Web Components', 'MCP',
  'Python', 'Node.js', 'Java', '.NET Core', 'React', 'Angular', 'Vue.js',
  'PostgreSQL', 'MongoDB', 'Oracle', 'SQL Server', 'AWS', 'Azure', 'Google Cloud',
  'GraphQL', 'REST APIs', 'Claude', 'Llama 3.1', 'Mistral', 'Qwen 2.5', 'Gemma 2', 'CodeLlama', 'WhatsApp Business API'
];

// Headline outcomes, quoted from the case studies on each service page
export const highlights = [
  { value: '45%', label: 'increase in sales productivity', context: 'Global retail chain', service: 'salesforce', anchor: 'stories' },
  { value: '280%', label: 'ROI in 18 months', context: 'Manufacturing company', service: 'erp', anchor: 'results' },
  { value: '50%', label: 'reduction in time-to-market', context: 'Enterprise software company', service: 'consultancy', anchor: 'impact' },
  { value: '90%', label: 'reduction in processing time', context: 'Financial institution', service: 'ai-agents', anchor: 'stories' }
];
